import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  Add,
  Receipt,
  HourglassTop,
  LocalShipping,
  Warning,
  Schedule,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, parseISO } from "date-fns";
import {
  getBusinessNow,
  getBusinessToday,
  getBusinessTodayDate,
} from "@/utils/businessTime";
import { fr } from "date-fns/locale";
import { StatCard, LoadingSpinner, PageHeader, Button } from "@/components/ui";
import { orderService, type OrderStatus } from "@/services/order.service";
import { deliveryService } from "@/services/delivery.service";
import { zoneService } from "@/services/zone.service";
import { formatCustomerName } from "@/utils/formatCustomerName";

/**
 * Dashboard opérationnel du jour — pilotage des commandes et livraisons.
 * Voir docs/Guide/proposition_dashboard_operationnel.md
 */
export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const today = getBusinessTodayDate();
  const todayStr = getBusinessToday();
  const operationTitle = format(today, "EEEE d MMMM yyyy", { locale: fr });

  const { data: ordersResponse, isLoading: ordersLoading } = useQuery({
    queryKey: ["orders", "dashboard", todayStr],
    queryFn: () =>
      orderService.getAll({
        deliveryDateFrom: todayStr,
        deliveryDateTo: todayStr,
      }),
  });
  const orders = Array.isArray(ordersResponse)
    ? ordersResponse
    : (ordersResponse?.orders ?? []);

  const { data: zones = [], isLoading: zonesLoading } = useQuery({
    queryKey: ["zones", "dashboard"],
    queryFn: () => zoneService.getAll(),
  });

  const { data: deliveriesToday = [], isLoading: deliveriesLoading } = useQuery({
    queryKey: ["deliveries", "dashboard", todayStr],
    queryFn: () =>
      deliveryService.getAll({
        deliveryDate: todayStr,
      }),
  });

  const pendingOrders = orders.filter((o) => o.status === "PENDING").length;
  const ordersToPrepare = orders.filter((o) =>
    ["CONFIRMED", "IN_PREPARATION"].includes(o.status)
  ).length;
  const ordersReady = orders.filter((o) => o.status === "READY").length;
  const ordersInDelivery = orders.filter((o) => o.status === "IN_DELIVERY").length;
  const ordersDelivered = orders.filter((o) => o.status === "DELIVERED").length;

  const stats = {
    totalOrders: orders.length,
    pendingOrders,
    inDelivery: ordersReady + ordersInDelivery,
    delivered: ordersDelivered,
    revenue: orders.reduce((sum, o) => sum + Number(o.totalAmount || 0), 0),
  };

  const zoneData = zones
    .map((zone) => {
      const zoneDeliveries = deliveriesToday.filter((d) => d.zoneId === zone.id);
      const total = zoneDeliveries.length;
      const delivered = zoneDeliveries.filter((d) => d.status === "DELIVERED").length;
      const firstWithDriver = zoneDeliveries.find((d) => d.driver);
      const driverName = firstWithDriver?.driver
        ? `${firstWithDriver.driver.firstName} ${firstWithDriver.driver.lastName}`
        : null;
      return {
        zoneId: zone.id,
        name: zone.name.replace(/^\d+ - /, ""),
        commandes: total,
        delivered,
        progress: total > 0 ? Math.round((delivered / total) * 100) : 0,
        driverName,
        recette: zoneDeliveries.reduce(
          (sum, d) => sum + Number(d.order?.totalAmount ?? 0),
          0
        ),
      };
    })
    .filter((data) => data.commandes > 0);

  // Fonction pour obtenir les propriétés du badge de statut
  const getStatusBadgeProps = (status: OrderStatus) => {
    const statusMap: Record<
      OrderStatus,
      {
        label: string;
        color: "primary" | "secondary" | "success" | "error" | "warning" | "info";
      }
    > = {
      PENDING: { label: "En Attente", color: "warning" },
      CONFIRMED: { label: "Confirmée", color: "info" },
      IN_PREPARATION: { label: "En Préparation", color: "info" },
      READY: { label: "Prête", color: "primary" },
      IN_DELIVERY: { label: "En Livraison", color: "primary" },
      DELIVERED: { label: "Livrée", color: "success" },
      CANCELLED: { label: "Annulée", color: "error" },
    };

    return statusMap[status] || { label: status, color: "default" };
  };

  // 10 dernières commandes du jour, triées par date de création (plus récentes en premier)
  const ordersOfTheDay = [...orders]
    .sort((a, b) => {
      const dateA = parseISO(a.createdAt || "").getTime();
      const dateB = parseISO(b.createdAt || "").getTime();
      return dateB - dateA;
    })
    .slice(0, 10);

  // Livraisons en cours (statut IN_PROGRESS)
  const deliveriesInProgress = deliveriesToday.filter(
    (d) => d.status === "IN_PROGRESS"
  );

  // Livraisons en retard : IN_PROGRESS et heure prévue dépassée
  // Comparaison en heure Guyane : `scheduledDeliveryTime` est exprimé en local Guyane.
  const businessNow = getBusinessNow();
  const delayedDeliveries = deliveriesInProgress.filter((d) => {
    const scheduled = d.scheduledDeliveryTime || "23:59";
    const [h, m] = scheduled.split(":").map(Number);
    const scheduledToday = new Date(businessNow);
    scheduledToday.setHours(h, m, 0, 0);
    return scheduledToday < businessNow;
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title={`Opération du ${operationTitle}`}
        subtitle="Pilotage des commandes et livraisons du jour."
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate("/orders/new")}
          >
            Nouvelle commande
          </Button>
        }
      />

      {/* Alertes : commandes en attente, retards */}
      {(stats.pendingOrders > 0 || delayedDeliveries.length > 0) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {stats.pendingOrders > 0 && (
            <Alert
              severity="warning"
              icon={<Warning />}
              action={
                <Button
                  variant="text"
                  size="small"
                  onClick={() =>
                    navigate("/orders", {
                      state: { statusFilter: "PENDING", deliveryDate: todayStr },
                    })
                  }
                >
                  Voir les commandes
                </Button>
              }
            >
              <AlertTitle>Commandes en attente</AlertTitle>
              {stats.pendingOrders} commande{stats.pendingOrders !== 1 ? "s" : ""} en attente de confirmation.
            </Alert>
          )}
          {delayedDeliveries.length > 0 && (
            <Alert
              severity="error"
              icon={<Schedule />}
              action={
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate("/deliveries/by-driver")}
                >
                  Voir les livraisons
                </Button>
              }
            >
              <AlertTitle>Retards</AlertTitle>
              {delayedDeliveries.length} livraison{delayedDeliveries.length !== 1 ? "s" : ""} en retard par rapport à l&apos;heure prévue.
            </Alert>
          )}
        </Box>
      )}

      {/* À faire maintenant */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              p: 2,
              cursor: "pointer",
              border: 1,
              borderColor: "divider",
              "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
            }}
            onClick={() =>
              navigate("/orders", {
                state: { statusFilter: "PENDING", deliveryDate: todayStr },
              })
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <HourglassTop color="warning" />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  À confirmer
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {pendingOrders} commande{pendingOrders !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              p: 2,
              cursor: "pointer",
              border: 1,
              borderColor: "divider",
              "&:hover": { borderColor: "error.main", bgcolor: "action.hover" },
            }}
            onClick={() => navigate("/deliveries/by-driver")}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Schedule color="error" />
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  En retard
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {delayedDeliveries.length} livraison{delayedDeliveries.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Indicateurs du jour */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            title="Commandes du jour"
            value={stats.totalOrders}
            icon={<Receipt />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            title="En livraison"
            value={stats.inDelivery}
            icon={<LocalShipping />}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <StatCard
            title="Livrées"
            value={stats.delivered}
            icon={<CheckCircle />}
          />
        </Grid>
      </Grid>

      {/* Phases du jour */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              À préparer
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {ordersToPrepare}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate("/orders", {
                  state: { deliveryDate: todayStr, statusFilter: "CONFIRMED" },
                })
              }
            >
              Voir
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Prêtes
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {ordersReady}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate("/orders", {
                  state: { deliveryDate: todayStr, statusFilter: "READY" },
                })
              }
            >
              Voir
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              En livraison
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {ordersInDelivery}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate("/orders", {
                  state: { deliveryDate: todayStr, statusFilter: "IN_DELIVERY" },
                })
              }
            >
              Voir
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block">
              Livrées
            </Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
              {ordersDelivered}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={() =>
                navigate("/orders", {
                  state: { deliveryDate: todayStr, statusFilter: "DELIVERED" },
                })
              }
            >
              Voir
            </Button>
          </Paper>
        </Grid>
      </Grid>

      {/* Tournées du jour par zone */}
      <Paper
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h3" sx={{ mb: 3, fontSize: "18px", fontWeight: 600 }}>
          Tournées du jour
        </Typography>
        {zonesLoading || deliveriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
            <LoadingSpinner />
          </Box>
        ) : zoneData.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune tournée pour aujourd&apos;hui
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {zoneData.map((zone) => (
              <Box
                key={zone.zoneId}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 2,
                  py: 1.5,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {zone.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {zone.driverName ?? "—"}
                  </Typography>
                </Box>
                <Box sx={{ flex: "1 1 120px", display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={zone.progress}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 1,
                        bgcolor: zone.progress === 100 ? "success.main" : "primary.main",
                      },
                    }}
                  />
                  <Typography variant="caption" fontWeight={600} sx={{ minWidth: 48 }}>
                    {zone.delivered}/{zone.commandes}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outline"
                  onClick={() =>
                    navigate("/deliveries/by-driver", {
                      state: { date: todayStr, zoneId: zone.zoneId },
                    })
                  }
                >
                  Voir la tournée
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* Livraisons en cours */}
      <Paper
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 600 }}>
            Livraisons en cours
          </Typography>
          {deliveriesInProgress.length > 0 && (
            <Button
              variant="outline"
              size="small"
              onClick={() => navigate("/deliveries/by-driver")}
            >
              Voir toutes les livraisons
            </Button>
          )}
        </Box>

        {deliveriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
            <LoadingSpinner />
          </Box>
        ) : deliveriesInProgress.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune livraison en cours pour le moment
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    N° Commande
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Livreur
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Zone
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Heure prévue
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Retard ?
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Client
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...deliveriesInProgress]
                  .sort((a, b) => {
                    const tA = a.scheduledDeliveryTime || "23:59";
                    const tB = b.scheduledDeliveryTime || "23:59";
                    return tA.localeCompare(tB);
                  })
                  .map((d) => {
                    const scheduled = d.scheduledDeliveryTime || "23:59";
                    const [h, m] = scheduled.split(":").map(Number);
                    const scheduledToday = new Date(d.deliveryDate);
                    scheduledToday.setHours(h, m, 0, 0);
                    const isDelayed = scheduledToday < businessNow;
                    return (
                      <TableRow
                        key={d.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/orders/${d.orderId}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: "monospace" }}>
                            {d.order?.orderNumber ?? "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {d.driver
                              ? `${d.driver.firstName} ${d.driver.lastName}`
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {d.zone?.name?.replace(/^\d+ - /, "") ?? "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                            {d.scheduledDeliveryTime ?? "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {isDelayed ? (
                            <Chip label="Oui" color="error" size="small" sx={{ fontSize: "11px" }} />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Non
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {d.order?.customer
                              ? formatCustomerName(d.order.customer)
                              : "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dernières commandes du jour */}
      <Paper
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 600 }}>
            Dernières commandes du jour
          </Typography>
          <Button
            variant="outline"
            size="small"
            onClick={() =>
              navigate("/orders", { state: { deliveryDate: todayStr } })
            }
          >
            Voir toutes les commandes du jour
          </Button>
        </Box>

        {ordersLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <LoadingSpinner />
          </Box>
        ) : ordersOfTheDay.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Aucune commande pour aujourd&apos;hui
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    N° Commande
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Client
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                    Zone
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 600, fontSize: "12px" }}
                  >
                    Statut
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 600, fontSize: "12px" }}
                  >
                    Montant
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordersOfTheDay.map((order) => {
                  const statusBadge = getStatusBadgeProps(order.status);
                  return (
                    <TableRow
                      key={order.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/orders/${order.id}`)}
                    >
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontFamily: "monospace" }}
                        >
                          {order.orderNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatCustomerName(order.customer ? {
                            firstName: order.customer.firstName,
                            lastName: order.customer.lastName,
                            type: order.customer.type as "INDIVIDUAL" | "INSTITUTION" | "COMPANY",
                          } : undefined) || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          {order.customerAddress?.zone?.name?.replace(/^\d+ - /, "") ||
                            "Non défini"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusBadge.label}
                          color={statusBadge.color}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "11px",
                            height: "auto",
                            py: 0.5,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontFamily: "monospace" }}
                        >
                          {Number(order.totalAmount || 0).toLocaleString(
                            "fr-FR",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                          €
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Synthèse — recettes par zone */}
      <Paper
        sx={{
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h3" sx={{ mb: 3, fontSize: "18px", fontWeight: 600 }}>
          Synthèse — Recettes par zone (aujourd&apos;hui)
        </Typography>
        {zonesLoading || deliveriesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
            <LoadingSpinner />
          </Box>
        ) : zoneData.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
            <Typography variant="body2" color="text.secondary">
              Aucune donnée disponible
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={zoneData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}€`}
              />
              <Tooltip
                formatter={(value: number) => [
                  `${value.toLocaleString("fr-FR")}€`,
                  "Recette",
                ]}
              />
              <Legend />
              <Bar
                dataKey="recette"
                name="Recette (€)"
                fill="#45B7D1"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
}
